'use client';
/** @jsxImportSource @emotion/react */

import { type PropsWithChildren, useMemo } from 'react';

import {
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
  type PickerValidDate,
} from '@mui/x-date-pickers-pro';

import { CoreIcon } from '../../core-icon/CoreIcon';

type ExcludedMuiProps =
  | 'dayOfWeekFormatter'
  | 'disableHighlightToday'
  | 'displayWeekNumber'
  | 'enableAccessibleFieldDOMStructure'
  | 'fixedWeekNumber'
  | 'formatDensity'
  | 'onSelectedSectionsChange'
  | 'orientation'
  | 'reduceAnimations'
  | 'referenceDate'
  | 'selectedSections'
  | 'showDaysOutsideCurrentMonth'
  | 'viewRenderers';

type DatePickerProps<TDate extends PickerValidDate> = Omit<
  MuiDatePickerProps<TDate, false>,
  ExcludedMuiProps
> & {
  /**
   * If `true`, the open picker button will be hidden.
   */
  hideOpenPickerButton?: boolean;
};

export type { DatePickerProps };

export function DatePicker<TDate extends PickerValidDate>({
  hideOpenPickerButton,
  slotProps,
  ...props
}: DatePickerProps<TDate>) {
  const stableSlotProps = useMemo(() => {
    if (hideOpenPickerButton) {
      return { ...slotProps, openPickerButton: { sx: { opacity: 0 } } };
    }
    return slotProps;
  }, [slotProps, hideOpenPickerButton]);
  return (
    <MuiDatePicker
      {...props}
      enableAccessibleFieldDOMStructure={false}
      slotProps={stableSlotProps}
      slots={{ ...props.slots, switchViewIcon: ChevronDown }}
    />
  );
}

function ChevronDown(prop: PropsWithChildren) {
  return <CoreIcon {...prop} name="chevrondown" size="sm" />;
}
